import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { User } from '../types';
import type { BullhornAuthData } from '../services/bullhornService';
import { testBullhornConnection } from '../services/bullhornService';
import {
  BULLHORN_SYNC_ENTITY_LABELS,
  type BullhornSyncEntityType,
} from '../services/bullhornSyncQueries';
import { runBullhornDiscover, runBullhornApply } from '../services/bullhornSyncEngine';
import {
  addExclusion,
  getRunChanges,
  loadBullhornSyncState,
  removeExclusion,
  searchSyncLogLines,
  searchSyncRuns,
  updateChangeExcluded,
  type SyncChangeRow,
  type SyncRunRow,
} from '../services/bullhornSyncStore';
import {
  getChangeOrigin,
  SYNC_ORIGIN_HINTS,
  SYNC_ORIGIN_LABELS,
  type SyncRecordOrigin,
} from '../services/bullhornSyncDiff';
import {
  AlertCircle,
  ArrowRight,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Download,
  History,
  Loader2,
  Play,
  RefreshCw,
  Search,
  TerminalSquare,
  Trash2,
  Upload,
  X,
} from 'lucide-react';

interface Props {
  user: User;
  bhAuth: BullhornAuthData | null;
  credentials: {
    clientId: string;
    clientSecret: string;
    apiUsername: string;
    apiPassword: string;
  };
  onClose: () => void;
}

type ConsoleTab = 'sync' | 'changes' | 'logs' | 'history';
type OriginFilter = 'all' | SyncRecordOrigin;

const OriginBadge: React.FC<{ origin: SyncRecordOrigin }> = ({ origin }) => {
  const title = SYNC_ORIGIN_HINTS[origin];
  switch (origin) {
    case 'new_in_bullhorn':
      return (
        <span
          title={title}
          className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-blue-50 text-blue-800 text-[10px] font-black uppercase tracking-wide ring-1 ring-blue-100"
        >
          <Download size={12} aria-hidden />
          {SYNC_ORIGIN_LABELS[origin]}
        </span>
      );
    case 'new_in_werftools':
      return (
        <span
          title={title}
          className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-violet-50 text-violet-800 text-[10px] font-black uppercase tracking-wide ring-1 ring-violet-100"
        >
          <Upload size={12} aria-hidden />
          {SYNC_ORIGIN_LABELS[origin]}
        </span>
      );
    default:
      return (
        <span
          title={title}
          className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-amber-50 text-amber-900 text-[10px] font-black uppercase tracking-wide ring-1 ring-amber-100"
        >
          <ArrowRight size={12} aria-hidden />
          {SYNC_ORIGIN_LABELS[origin]}
        </span>
      );
  }
};

const BullhornSyncConsole: React.FC<Props> = ({ user, bhAuth: initialAuth, credentials, onClose }) => {
  const clientId = user.clientId as string;
  const [bhAuth, setBhAuth] = useState<BullhornAuthData | null>(initialAuth);
  const [syncState, setSyncState] = useState<{ initialFullSyncCompleted?: boolean; lastSyncAt?: string }>({});
  const [activeTab, setActiveTab] = useState<ConsoleTab>('sync');
  const [isRunning, setIsRunning] = useState(false);
  const [activeRunId, setActiveRunId] = useState<string | null>(null);
  const [awaitingApply, setAwaitingApply] = useState(false);
  const [liveLogs, setLiveLogs] = useState<
    Array<{ time: Date; entity: string; message: string; level: string }>
  >([]);
  const [changes, setChanges] = useState<SyncChangeRow[]>([]);
  const [selectedChangeIds, setSelectedChangeIds] = useState<Record<string, boolean>>({});
  const [entityFilter, setEntityFilter] = useState<BullhornSyncEntityType | 'all'>('all');
  const [originFilter, setOriginFilter] = useState<OriginFilter>('all');
  const [logSearch, setLogSearch] = useState('');
  const [historySearch, setHistorySearch] = useState('');
  const [historyRuns, setHistoryRuns] = useState<SyncRunRow[]>([]);
  const [persistedLogs, setPersistedLogs] = useState<
    Array<{ id: string; run_id: string; created_at: string; entity_type: string; level: string; message: string; bullhorn_id?: string | null }>
  >([]);
  const [expandedRunId, setExpandedRunId] = useState<string | null>(null);
  const [errorBanner, setErrorBanner] = useState<string | null>(null);

  const pushLiveLog = useCallback((entity: string, message: string, level = 'info') => {
    setLiveLogs((prev) => [{ time: new Date(), entity, message, level }, ...prev].slice(0, 2000));
  }, []);

  const refreshState = useCallback(async () => {
    const st = await loadBullhornSyncState(clientId);
    setSyncState(st);
  }, [clientId]);

  const refreshChanges = useCallback(async () => {
    if (!activeRunId) return;
    const rows = await getRunChanges(activeRunId);
    setChanges(rows);
    const sel: Record<string, boolean> = {};
    rows.forEach((r) => {
      if (!r.excluded) sel[r.id] = true;
    });
    setSelectedChangeIds(sel);
  }, [activeRunId]);

  const loadHistory = useCallback(async () => {
    const runs = await searchSyncRuns(clientId, historySearch);
    setHistoryRuns(runs);
  }, [clientId, historySearch]);

  const loadPersistedLogs = useCallback(async () => {
    const rows = await searchSyncLogLines(clientId, logSearch, activeRunId || undefined, 800);
    setPersistedLogs(rows);
  }, [clientId, logSearch, activeRunId]);

  useEffect(() => {
    void refreshState();
  }, [refreshState]);

  useEffect(() => {
    if (activeTab === 'changes') void refreshChanges();
  }, [activeTab, refreshChanges]);

  useEffect(() => {
    if (activeTab === 'history') void loadHistory();
  }, [activeTab, loadHistory]);

  useEffect(() => {
    if (activeTab === 'logs') void loadPersistedLogs();
  }, [activeTab, loadPersistedLogs]);

  const ensureAuth = async (): Promise<BullhornAuthData> => {
    if (bhAuth?.BhRestToken && bhAuth.restUrl) return bhAuth;
    const data = await testBullhornConnection(credentials);
    const auth = { BhRestToken: data.BhRestToken, restUrl: data.restUrl };
    setBhAuth(auth);
    return auth;
  };

  const runFullSync = async () => {
    setErrorBanner(null);
    setIsRunning(true);
    setLiveLogs([]);
    setAwaitingApply(false);
    try {
      const auth = await ensureAuth();
      pushLiveLog('system', 'Volledige synchronisatie gestart…', 'info');
      const { runId, awaitingApply: pending } = await runBullhornDiscover({
        clientId,
        userId: user.id,
        auth,
        mode: 'full',
        onProgress: (ev) => pushLiveLog(ev.entity || 'system', ev.message, ev.level || 'info'),
      });
      setActiveRunId(runId);
      setAwaitingApply(pending);
      await refreshState();
      await refreshChanges();
      setActiveTab('logs');
    } catch (e: unknown) {
      setErrorBanner(e instanceof Error ? e.message : String(e));
    } finally {
      setIsRunning(false);
    }
  };

  const runIncrementalDiscover = async () => {
    setErrorBanner(null);
    setIsRunning(true);
    setLiveLogs([]);
    try {
      const auth = await ensureAuth();
      pushLiveLog('system', 'Wijzigingen ophalen…', 'info');
      const { runId, awaitingApply: pending } = await runBullhornDiscover({
        clientId,
        userId: user.id,
        auth,
        mode: 'incremental',
        onProgress: (ev) => pushLiveLog(ev.entity || 'system', ev.message, ev.level || 'info'),
      });
      setActiveRunId(runId);
      setAwaitingApply(pending);
      await refreshChanges();
      setActiveTab('changes');
    } catch (e: unknown) {
      setErrorBanner(e instanceof Error ? e.message : String(e));
    } finally {
      setIsRunning(false);
    }
  };

  const applySelected = async () => {
    if (!activeRunId) return;
    setErrorBanner(null);
    setIsRunning(true);
    try {
      const auth = await ensureAuth();
      // Mark unselected as excluded
      for (const ch of changes) {
        const shouldSync = selectedChangeIds[ch.id] && !ch.excluded;
        if (!shouldSync && !ch.excluded) {
          await updateChangeExcluded(ch.id, true);
          await addExclusion(clientId, ch.entity_type as BullhornSyncEntityType, ch.bullhorn_id, user.id);
        } else if (shouldSync && ch.excluded) {
          await updateChangeExcluded(ch.id, false);
          await removeExclusion(clientId, ch.entity_type as BullhornSyncEntityType, ch.bullhorn_id);
        }
      }
      await runBullhornApply({
        runId: activeRunId,
        clientId,
        userId: user.id,
        auth,
        onProgress: (ev) => pushLiveLog(ev.entity || 'system', ev.message, ev.level || 'info'),
      });
      setAwaitingApply(false);
      await refreshState();
      await refreshChanges();
      setActiveTab('logs');
    } catch (e: unknown) {
      setErrorBanner(e instanceof Error ? e.message : String(e));
    } finally {
      setIsRunning(false);
    }
  };

  const originCounts = useMemo(() => {
    const counts: Record<SyncRecordOrigin, number> = {
      new_in_bullhorn: 0,
      updated_both: 0,
      new_in_werftools: 0,
    };
    for (const ch of changes) {
      counts[getChangeOrigin(ch)]++;
    }
    return counts;
  }, [changes]);

  const filteredChanges = useMemo(() => {
    return changes.filter((c) => {
      if (entityFilter !== 'all' && c.entity_type !== entityFilter) return false;
      if (originFilter !== 'all' && getChangeOrigin(c) !== originFilter) return false;
      return true;
    });
  }, [changes, entityFilter, originFilter]);

  const pendingCount = changes.filter((c) => !c.excluded && c.status === 'pending').length;

  const toggleAll = (checked: boolean) => {
    const next: Record<string, boolean> = {};
    filteredChanges.forEach((c) => {
      next[c.id] = checked;
    });
    setSelectedChangeIds((prev) => ({ ...prev, ...next }));
  };

  const excludeSelected = async () => {
    for (const ch of filteredChanges) {
      if (selectedChangeIds[ch.id]) {
        await updateChangeExcluded(ch.id, true);
        await addExclusion(clientId, ch.entity_type as BullhornSyncEntityType, ch.bullhorn_id, user.id);
      }
    }
    await refreshChanges();
  };

  return (
    <div className="fixed inset-0 z-[60] bg-slate-100 flex flex-col font-sans">
      <div className="bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between shadow-sm shrink-0">
        <div>
          <h1 className="text-lg font-black text-slate-900 tracking-tight flex items-center gap-2">
            <RefreshCw size={22} className="text-[#e51b23]" />
            Bullhorn synchronisatie
          </h1>
          <p className="text-[11px] text-slate-500 font-medium mt-0.5">
            {syncState.initialFullSyncCompleted
              ? `Laatste sync: ${syncState.lastSyncAt ? new Date(syncState.lastSyncAt).toLocaleString('nl-NL') : '—'} · daarna alleen gewijzigde data`
              : 'Nog geen volledige eerste sync uitgevoerd'}
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center hover:bg-slate-200"
        >
          <X size={18} />
        </button>
      </div>

      {errorBanner && (
        <div className="mx-6 mt-4 p-3 bg-red-50 border border-red-100 rounded-xl text-xs font-bold text-red-700 flex items-start gap-2">
          <AlertCircle size={16} className="shrink-0 mt-0.5" />
          {errorBanner}
        </div>
      )}

      <div className="flex flex-1 overflow-hidden">
        <aside className="w-72 shrink-0 bg-white border-r border-slate-200 p-4 flex flex-col gap-3">
          {!syncState.initialFullSyncCompleted ? (
            <button
              type="button"
              disabled={isRunning}
              onClick={() => void runFullSync()}
              className="w-full py-3 px-4 bg-[#e51b23] text-white rounded-xl text-xs font-black uppercase tracking-widest flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {isRunning ? <Loader2 size={14} className="animate-spin" /> : <Play size={14} />}
              Eerste volledige sync
            </button>
          ) : (
            <>
              <button
                type="button"
                disabled={isRunning}
                onClick={() => void runIncrementalDiscover()}
                className="w-full py-3 px-4 bg-emerald-600 text-white rounded-xl text-xs font-black uppercase tracking-widest flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {isRunning ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
                Sync wijzigingen
              </button>
              <button
                type="button"
                disabled={isRunning}
                onClick={() => void runFullSync()}
                className="w-full py-2 px-4 bg-slate-100 text-slate-700 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-slate-200 disabled:opacity-50"
              >
                Opnieuw alles syncen
              </button>
            </>
          )}

          {awaitingApply && (
            <button
              type="button"
              disabled={isRunning}
              onClick={() => void applySelected()}
              className="w-full py-3 px-4 bg-indigo-600 text-white rounded-xl text-xs font-black uppercase tracking-widest flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {isRunning ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
              Uitvoeren ({pendingCount})
            </button>
          )}

          <nav className="mt-4 space-y-1">
            {(
              [
                ['sync', 'Overzicht'],
                ['changes', `Wijzigingen${changes.length ? ` (${changes.length})` : ''}`],
                ['logs', 'Live log'],
                ['history', 'Geschiedenis'],
              ] as const
            ).map(([id, label]) => (
              <button
                key={id}
                type="button"
                onClick={() => setActiveTab(id)}
                className={`w-full text-left px-3 py-2 rounded-lg text-xs font-bold ${
                  activeTab === id ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-50'
                }`}
              >
                {label}
              </button>
            ))}
          </nav>

          <div className="mt-auto text-[10px] text-slate-400 font-medium leading-relaxed">
            Sync: CRM, kandidaten, vacatures, plaatsingen, pipelines en pipeline-geschiedenis.
          </div>
        </aside>

        <main className="flex-1 flex flex-col overflow-hidden">
          {activeTab === 'sync' && (
            <div className="p-6 overflow-y-auto space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {Object.entries(BULLHORN_SYNC_ENTITY_LABELS).map(([key, label]) => (
                  <div key={key} className="bg-white rounded-xl border border-slate-100 p-4">
                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{label}</span>
                  </div>
                ))}
              </div>
              {liveLogs.slice(0, 15).map((l, i) => (
                <p key={i} className="text-xs font-mono text-slate-600">
                  [{l.time.toLocaleTimeString('nl-NL')}] {l.entity}: {l.message}
                </p>
              ))}
            </div>
          )}

          {activeTab === 'changes' && (
            <div className="flex flex-col flex-1 overflow-hidden">
              <div className="p-4 border-b border-slate-100 bg-slate-50/80 grid grid-cols-1 sm:grid-cols-3 gap-3">
                <button
                  type="button"
                  onClick={() => setOriginFilter(originFilter === 'new_in_bullhorn' ? 'all' : 'new_in_bullhorn')}
                  className={`text-left p-3 rounded-xl border-2 transition-all ${
                    originFilter === 'new_in_bullhorn'
                      ? 'border-blue-400 bg-blue-50 shadow-sm'
                      : 'border-blue-100 bg-white hover:border-blue-200'
                  }`}
                >
                  <span className="text-[10px] font-black text-blue-600 uppercase tracking-widest flex items-center gap-1">
                    <Download size={12} /> Bullhorn
                  </span>
                  <span className="block text-2xl font-black text-slate-900 tabular-nums mt-1">
                    {originCounts.new_in_bullhorn}
                  </span>
                  <span className="text-[10px] text-slate-500 font-medium">Nieuw in Bullhorn (import)</span>
                </button>
                <button
                  type="button"
                  onClick={() => setOriginFilter(originFilter === 'updated_both' ? 'all' : 'updated_both')}
                  className={`text-left p-3 rounded-xl border-2 transition-all ${
                    originFilter === 'updated_both'
                      ? 'border-amber-400 bg-amber-50 shadow-sm'
                      : 'border-amber-100 bg-white hover:border-amber-200'
                  }`}
                >
                  <span className="text-[10px] font-black text-amber-700 uppercase tracking-widest flex items-center gap-1">
                    <ArrowRight size={12} /> Beide
                  </span>
                  <span className="block text-2xl font-black text-slate-900 tabular-nums mt-1">
                    {originCounts.updated_both}
                  </span>
                  <span className="text-[10px] text-slate-500 font-medium">Al gekoppeld — wijziging bijwerken</span>
                </button>
                <button
                  type="button"
                  onClick={() => setOriginFilter(originFilter === 'new_in_werftools' ? 'all' : 'new_in_werftools')}
                  className={`text-left p-3 rounded-xl border-2 transition-all ${
                    originFilter === 'new_in_werftools'
                      ? 'border-violet-400 bg-violet-50 shadow-sm'
                      : 'border-violet-100 bg-white hover:border-violet-200'
                  }`}
                >
                  <span className="text-[10px] font-black text-violet-700 uppercase tracking-widest flex items-center gap-1">
                    <Upload size={12} /> Werftools
                  </span>
                  <span className="block text-2xl font-black text-slate-900 tabular-nums mt-1">
                    {originCounts.new_in_werftools}
                  </span>
                  <span className="text-[10px] text-slate-500 font-medium">Alleen lokaal (nog niet in BH)</span>
                </button>
              </div>

              <div className="p-4 border-b border-slate-200 bg-white flex flex-wrap gap-2 items-center">
                <select
                  value={entityFilter}
                  onChange={(e) => setEntityFilter(e.target.value as BullhornSyncEntityType | 'all')}
                  className="text-xs font-bold border border-slate-200 rounded-lg px-2 py-1.5"
                >
                  <option value="all">Alle types</option>
                  {Object.entries(BULLHORN_SYNC_ENTITY_LABELS).map(([k, v]) => (
                    <option key={k} value={k}>
                      {v}
                    </option>
                  ))}
                </select>
                <select
                  value={originFilter}
                  onChange={(e) => setOriginFilter(e.target.value as OriginFilter)}
                  className="text-xs font-bold border border-slate-200 rounded-lg px-2 py-1.5"
                >
                  <option value="all">Alle herkomst</option>
                  <option value="new_in_bullhorn">{SYNC_ORIGIN_LABELS.new_in_bullhorn}</option>
                  <option value="updated_both">{SYNC_ORIGIN_LABELS.updated_both}</option>
                  <option value="new_in_werftools">{SYNC_ORIGIN_LABELS.new_in_werftools}</option>
                </select>
                <button
                  type="button"
                  onClick={() => toggleAll(true)}
                  className="text-[10px] font-black uppercase text-slate-600 px-2 py-1 bg-slate-100 rounded-lg"
                >
                  Alles selecteren
                </button>
                <button
                  type="button"
                  onClick={() => toggleAll(false)}
                  className="text-[10px] font-black uppercase text-slate-600 px-2 py-1 bg-slate-100 rounded-lg"
                >
                  Niets selecteren
                </button>
                <button
                  type="button"
                  onClick={() => void excludeSelected()}
                  className="text-[10px] font-black uppercase text-red-600 px-2 py-1 bg-red-50 rounded-lg flex items-center gap-1"
                >
                  <Trash2 size={12} /> Uitsluiten (niet syncen)
                </button>
                <button type="button" onClick={() => void refreshChanges()} className="ml-auto text-slate-500">
                  <RefreshCw size={16} />
                </button>
              </div>
              <div className="flex-1 overflow-auto">
                <table className="w-full text-left text-xs">
                  <thead className="bg-slate-50 sticky top-0">
                    <tr>
                      <th className="p-2 w-8" />
                      <th className="p-2 font-black uppercase text-slate-400">Herkomst</th>
                      <th className="p-2 font-black uppercase text-slate-400">Type</th>
                      <th className="p-2 font-black uppercase text-slate-400">Naam</th>
                      <th className="p-2 font-black uppercase text-slate-400">ID</th>
                      <th className="p-2 font-black uppercase text-slate-400">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredChanges.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="p-8 text-center text-slate-500">
                          Geen wijzigingen{originFilter !== 'all' || entityFilter !== 'all' ? ' voor dit filter' : ''}.
                          Start een sync om Bullhorn vs Werftools te vergelijken.
                        </td>
                      </tr>
                    ) : (
                      filteredChanges.map((ch) => {
                        const origin = getChangeOrigin(ch);
                        const isLocalOnly = origin === 'new_in_werftools';
                        const rowAccent =
                          origin === 'new_in_bullhorn'
                            ? 'border-l-4 border-l-blue-400'
                            : origin === 'new_in_werftools'
                              ? 'border-l-4 border-l-violet-400'
                              : 'border-l-4 border-l-amber-300';
                        return (
                        <tr
                          key={ch.id}
                          className={`border-b border-slate-100 ${rowAccent} ${ch.excluded ? 'opacity-50 bg-slate-50' : ''}`}
                        >
                          <td className="p-2">
                            <input
                              type="checkbox"
                              checked={Boolean(selectedChangeIds[ch.id]) && !ch.excluded}
                              disabled={ch.excluded || ch.status !== 'pending' || isLocalOnly}
                              title={
                                isLocalOnly
                                  ? 'Alleen in Werftools — exporteer via de kandidaat-/vacature-mapper naar Bullhorn'
                                  : undefined
                              }
                              onChange={(e) =>
                                setSelectedChangeIds((prev) => ({ ...prev, [ch.id]: e.target.checked }))
                              }
                            />
                          </td>
                          <td className="p-2">
                            <OriginBadge origin={origin} />
                          </td>
                          <td className="p-2 font-medium">
                            {BULLHORN_SYNC_ENTITY_LABELS[ch.entity_type as BullhornSyncEntityType] || ch.entity_type}
                          </td>
                          <td className="p-2">{ch.display_name}</td>
                          <td className="p-2 font-mono text-slate-500 text-[10px]">
                            {isLocalOnly
                              ? `WT:${String((ch.metadata as { localId?: string })?.localId || ch.bullhorn_id).replace(/^local:/, '').slice(0, 8)}…`
                              : `BH:${ch.bullhorn_id}`}
                          </td>
                          <td className="p-2">
                            <span
                              className={`px-1.5 py-0.5 rounded text-[10px] font-bold uppercase ${
                                ch.status === 'synced'
                                  ? 'bg-emerald-100 text-emerald-700'
                                  : ch.status === 'excluded'
                                    ? 'bg-slate-200 text-slate-600'
                                    : ch.status === 'error'
                                      ? 'bg-red-100 text-red-700'
                                      : 'bg-amber-100 text-amber-800'
                              }`}
                            >
                              {ch.status}
                            </span>
                          </td>
                        </tr>
                      );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {(activeTab === 'logs' || activeTab === 'history') && (
            <div className="flex flex-col flex-1 overflow-hidden bg-slate-900">
              <div className="px-4 py-3 border-b border-slate-800 flex items-center gap-2">
                <TerminalSquare size={14} className="text-slate-500" />
                <span className="text-xs font-mono font-bold text-slate-400 uppercase tracking-widest">
                  {activeTab === 'logs' ? 'Gedetailleerd logboek' : 'Sync-geschiedenis'}
                </span>
                <div className="ml-auto flex items-center gap-2">
                  <Search size={14} className="text-slate-500" />
                  <input
                    type="search"
                    placeholder="Zoeken…"
                    value={activeTab === 'logs' ? logSearch : historySearch}
                    onChange={(e) =>
                      activeTab === 'logs' ? setLogSearch(e.target.value) : setHistorySearch(e.target.value)
                    }
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        activeTab === 'logs' ? void loadPersistedLogs() : void loadHistory();
                      }
                    }}
                    className="bg-slate-800 border border-slate-700 rounded-lg px-2 py-1 text-xs text-slate-200 w-48"
                  />
                  <button
                    type="button"
                    onClick={() => (activeTab === 'logs' ? void loadPersistedLogs() : void loadHistory())}
                    className="text-[10px] font-bold text-indigo-400 uppercase"
                  >
                    Zoek
                  </button>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-4 font-mono text-xs">
                {activeTab === 'history' ? (
                  <div className="space-y-2 font-sans">
                    {historyRuns.map((run) => (
                      <div key={run.id} className="bg-slate-800/50 rounded-lg border border-slate-700/50">
                        <button
                          type="button"
                          className="w-full p-3 flex items-center justify-between text-left hover:bg-slate-800"
                          onClick={() => {
                            setExpandedRunId(expandedRunId === run.id ? null : run.id);
                            setActiveRunId(run.id);
                          }}
                        >
                          <div>
                            <span className="text-slate-200 font-bold">{run.mode}</span>
                            <span className="text-slate-500 ml-2">
                              {new Date(run.started_at).toLocaleString('nl-NL')}
                            </span>
                            <span
                              className={`ml-2 px-1.5 py-0.5 rounded text-[10px] uppercase ${
                                run.status === 'success'
                                  ? 'text-emerald-400'
                                  : run.status === 'error'
                                    ? 'text-red-400'
                                    : 'text-amber-400'
                              }`}
                            >
                              {run.status}
                            </span>
                          </div>
                          <div className="flex items-center gap-3 text-slate-400">
                            <span>{run.total_synced ?? 0} gesync</span>
                            {expandedRunId === run.id ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                          </div>
                        </button>
                        {expandedRunId === run.id && (
                          <div className="px-3 pb-3 text-slate-400 text-[11px]">
                            Ontdekt: {run.total_discovered} · Uitgesloten: {run.total_excluded} · Fouten:{' '}
                            {run.total_errors}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <table className="w-full">
                    <tbody>
                      {(liveLogs.length ? liveLogs : []).concat(
                        persistedLogs.map((l) => ({
                          time: new Date(l.created_at),
                          entity: l.entity_type,
                          message: l.message,
                          level: l.level,
                          key: l.id,
                        }))
                      ).length === 0 ? (
                        <tr>
                          <td className="py-8 text-center text-slate-600">Geen logregels.</td>
                        </tr>
                      ) : (
                        <>
                          {liveLogs.map((log, i) => (
                            <tr key={`live-${i}`} className="border-b border-slate-800/50">
                              <td className="py-1.5 text-slate-500 w-24">{log.time.toLocaleTimeString('nl-NL')}</td>
                              <td className="py-1.5 text-slate-400 w-28">{log.entity}</td>
                              <td
                                className={`py-1.5 ${
                                  log.level === 'error'
                                    ? 'text-red-400'
                                    : log.level === 'success'
                                      ? 'text-emerald-400'
                                      : 'text-slate-300'
                                }`}
                              >
                                {log.message}
                              </td>
                            </tr>
                          ))}
                          {persistedLogs.map((l) => (
                            <tr key={l.id} className="border-b border-slate-800/50">
                              <td className="py-1.5 text-slate-500 w-24">
                                {new Date(l.created_at).toLocaleTimeString('nl-NL')}
                              </td>
                              <td className="py-1.5 text-slate-400 w-28">{l.entity_type}</td>
                              <td
                                className={`py-1.5 ${
                                  l.level === 'error'
                                    ? 'text-red-400'
                                    : l.level === 'success'
                                      ? 'text-emerald-400'
                                      : 'text-slate-300'
                                }`}
                              >
                                {l.message}
                                {l.bullhorn_id ? ` [BH:${l.bullhorn_id}]` : ''}
                              </td>
                            </tr>
                          ))}
                        </>
                      )}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default BullhornSyncConsole;
